import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { FlowNode } from '../../../types/flowBuilder';
import { ChatBubbleLeftRightIcon, BoltIcon } from '@heroicons/react/24/outline';
import { getNodeColors } from '../utils/nodeColors';

const TelegramTriggerNode: React.FC<NodeProps<FlowNode['data']>> = ({ data, selected }) => {
  const nodeColors = getNodeColors('telegram_trigger');

  const botToken = data.config?.botToken || 'Nincs beállítva';
  const allowedChats = data.config?.allowedChats || [];
  const commands = data.config?.commands || [];
  const listenToAll = data.config?.listenToAll || false;
  
  // Bot token státusz
  const hasToken = botToken && botToken !== 'Nincs beállítva';
  
  // Chat számok
  const chatCount = allowedChats.length;
  const commandCount = commands.length;

  return (
    <div className={`px-4 py-3 shadow-lg rounded-lg bg-gradient-to-r ${nodeColors.gradient.from} ${nodeColors.gradient.to} border-2 transition-all duration-200 ${
      selected ? nodeColors.border.selected + ' shadow-xl scale-105' : nodeColors.border.default
    }`}>
      <div className="flex items-center space-x-3">
        {/* Icon */}
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
            <div className="relative">
              <ChatBubbleLeftRightIcon className="w-5 h-5 text-white" />
              <BoltIcon className="w-3 h-3 text-yellow-300 absolute -top-1 -right-1" />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white truncate">
            {data.label}
          </div>
          {data.description && (
            <div className={`text-xs ${nodeColors.text.secondary} truncate mt-1`}>
              {data.description}
            </div>
          )}
          
          {/* Trigger Status */}
          <div className="flex items-center mt-1 space-x-1">
            <span className="text-sm text-white">📨</span>
            <span className={`text-xs ${nodeColors.text.secondary}`}>
              {hasToken ? 'Webhook aktív' : 'Webhook nincs beállítva'}
            </span>
          </div>
        </div>

        {/* Status indicator */}
        <div className="flex-shrink-0">
          <div className={`w-3 h-3 rounded-full ${
            data.hasError ? 'bg-red-700' : hasToken ? 'bg-green-400' : 'bg-yellow-400'
          }`} />
        </div>
      </div>

      {/* Configuration preview */}
      <div className="mt-2 space-y-1">
        {/* Listen Mode */}
        <div className={`text-xs ${nodeColors.text.secondary} bg-black bg-opacity-20 px-2 py-1 rounded flex items-center justify-between`}>
          <div>
            <strong>Figyelés:</strong>
            <span className="ml-1">{listenToAll ? 'Minden üzenet' : 'Szűrt üzenetek'}</span>
          </div>
          {listenToAll ? <span className="text-xs">🌐</span> : <span className="text-xs">🎯</span>}
        </div>
        
        {/* Chat Count */}
        {!listenToAll && (
          <div className={`text-xs ${nodeColors.text.secondary} bg-black bg-opacity-20 px-2 py-1 rounded`}>
            <strong>Engedélyezett chat-ek:</strong>
            <span className="ml-1">{chatCount === 0 ? 'Nincs megadva' : `${chatCount} db`}</span>
          </div>
        )}
        
        {/* Commands */}
        <div className={`text-xs ${nodeColors.text.secondary} bg-black bg-opacity-20 px-2 py-1 rounded`}>
          <strong>Parancsok:</strong>
          <span className="ml-1">
            {commandCount === 0 ? 'Minden üzenet' : `${commandCount} parancs`}
          </span>
          {commandCount > 0 && (
            <div className="truncate mt-1 opacity-90 font-mono">
              {commands.slice(0, 3).join(', ')}
              {commandCount > 3 && '...'}
            </div>
          )}
        </div>
      </div>

      {/* Error message */}
      {data.hasError && data.errorMessage && (
        <div className="mt-2 text-xs text-red-200 bg-red-700 bg-opacity-30 px-2 py-1 rounded">
          {data.errorMessage}
        </div>
      )}

      {/* Webhook status */}
      {data.executionData && (
        <div className="mt-2 text-xs text-blue-100 bg-black bg-opacity-20 px-2 py-1 rounded">
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${
              data.executionData.success ? 'bg-green-400' : 'bg-red-400'
            }`} />
            <span>
              {data.executionData.success ? 'Webhook aktív' : 'Webhook hiba'}
            </span>
          </div>
          {data.executionData.webhook_url && (
            <div className="font-mono text-xs mt-1 truncate">
              {data.executionData.webhook_url}
            </div>
          )}
        </div>
      )}

      {/* Output handle - Triggereknek nincs input handle-je */}
      <Handle
        type="source"
        position={Position.Right}
        className={`w-3 h-3 ${nodeColors.handle} border-2 border-white`}
      />
    </div>
  );
};

export default TelegramTriggerNode;
